import { AccessLevel, isEnoughAccessToReadWithoutWeights } from '@upright/common/iam/access/types';
import { ModelEntityType, NamedModelEntityReference } from '@upright/common/model/input/types';
import {
  getCompanyBiggestNegativeTakeaway,
  getCompanyBiggestPositiveTakeaway,
} from '@upright/common/model/output/scores/key-takeaways/company/company-key-takeaways';
import { CompanyKeyTakeaway } from '@upright/common/model/output/scores/key-takeaways/company/types-company-key-takeaway';
import { KeyTakeawayType } from '@upright/common/model/output/scores/key-takeaways/types';
import {
  getCompanyGroupBiggestNegativeTakeaway,
  getCompanyGroupBiggestPositiveTakeaway,
} from '@upright/common/model/output/scores/key-takeaways/weight-set/company-group-key-takeaways';
import {
  WeightSetKeyTakeaway,
  WeightSetKeyTakeawayBiggestNegative,
  WeightSetKeyTakeawayBiggestPositive,
} from '@upright/common/model/output/scores/key-takeaways/weight-set/types-weight-set-key-takeaway';
import { getKeyTakeawayJsx } from '@upright/ui/views/model-entity-views/shared/key-takeaways/get-key-takeaway-jsx';
import { Dictionary, flatten, get, keyBy, mapValues } from 'lodash';

type Props = {
  entityName?: string | undefined;
  disableLinks?: boolean;
  isLoadingData: boolean;
} & (
  | {
      entityType: ModelEntityType.COMPANY;
      takeaway: CompanyKeyTakeaway;
      productNames: NamedModelEntityReference[] | undefined;
      hasProductViewAccess: boolean;
    }
  | {
      entityType: ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;
      takeaway: WeightSetKeyTakeaway;
      weightSetItemsById: Dictionary<{ name?: string; accessLevel?: AccessLevel }> | undefined;
    }
);

// Not used for groups / portfolios as of 08/2024, feel free to clean up -ML

export function KeyTakeawayContent(props: Props) {
  switch (props.entityType) {
    case ModelEntityType.COMPANY: {
      return (
        <>
          {companyTakeawayToText(
            props.takeaway,
            props.productNames || [],
            props.entityName,
            props.hasProductViewAccess,
            props.isLoadingData,
            props.disableLinks,
          )}
        </>
      );
    }
    case ModelEntityType.GROUP:
    case ModelEntityType.PORTFOLIO: {
      return (
        <>
          {weightSetTakeawayToText(
            props.takeaway,
            props.weightSetItemsById || {},
            props.entityName,
            props.isLoadingData,
            props.disableLinks,
          )}
        </>
      );
    }
    default:
      return <></>;
  }
}

function companyTakeawayToText(
  ta: CompanyKeyTakeaway,
  productNames: NamedModelEntityReference[],
  companyName: string | undefined,
  hasProductViewAccess: boolean,
  isLoadingData: boolean,
  disableLinks?: boolean,
) {
  const productNamesByCode = mapValues(
    keyBy(productNames, d => d.id),
    d => d.name,
  );

  const itemCount = Object.keys(productNamesByCode).length;
  const canAccessProducts = hasProductViewAccess;
  const allProductCodesOfTakeaway = canAccessProducts ? Object.keys(productNamesByCode) : [];

  const params = {
    takeaway: ta,
    productNamesByCode,
    productCount: itemCount,
    companyName: companyName ?? 'this company',
  };
  switch (ta.type) {
    case KeyTakeawayType.BIGGEST_POSITIVE: {
      return getKeyTakeawayJsx(
        getCompanyBiggestPositiveTakeaway(params),
        isLoadingData,
        allProductCodesOfTakeaway,
        ModelEntityType.PRODUCT,
        disableLinks,
      );
    }
    case KeyTakeawayType.BIGGEST_NEGATIVE: {
      return getKeyTakeawayJsx(
        getCompanyBiggestNegativeTakeaway(params),
        isLoadingData,
        allProductCodesOfTakeaway,
        ModelEntityType.PRODUCT,
        disableLinks,
      );
    }
    default: {
      return <></>;
    }
  }
}

function weightSetTakeawayToText(
  ta: WeightSetKeyTakeaway,
  weightSetItemsById: Dictionary<{ name?: string; accessLevel?: AccessLevel }>,
  groupName: string | undefined,
  isLoadingData: boolean,
  disableLinks?: boolean,
) {
  const itemCount = Object.keys(weightSetItemsById).length;
  const params = {
    takeaway: ta,
    namesById: mapValues(weightSetItemsById, d => d.name),
    groupName: groupName ?? 'this entity',
    itemCount,
  };
  switch (ta.type) {
    case KeyTakeawayType.BIGGEST_POSITIVE: {
      const listableEntities = getReadableEntitiesContributingToTakeAway(ta, weightSetItemsById);
      return getKeyTakeawayJsx(
        getCompanyGroupBiggestPositiveTakeaway(params),
        isLoadingData,
        listableEntities,
        ModelEntityType.COMPANY,
        disableLinks,
      );
    }
    case KeyTakeawayType.BIGGEST_NEGATIVE: {
      const listableEntities = getReadableEntitiesContributingToTakeAway(ta, weightSetItemsById);
      return getKeyTakeawayJsx(
        getCompanyGroupBiggestNegativeTakeaway(params),
        isLoadingData,
        listableEntities,
        ModelEntityType.COMPANY,
        disableLinks,
      );
    }
    default: {
      return <></>;
    }
  }
}

function getReadableEntitiesContributingToTakeAway(
  ta: WeightSetKeyTakeawayBiggestPositive | WeightSetKeyTakeawayBiggestNegative,
  weightSetItemsById: Dictionary<{ accessLevel?: AccessLevel }>,
) {
  const entityIds = flatten(ta.impactCategories.map(i => i.contributingWeightSetItems.map(d => d.entityId)));
  const entityIdsWithAccess = entityIds.filter(entityId => {
    const itemInfo = get(weightSetItemsById, entityId, undefined);
    return isEnoughAccessToReadWithoutWeights(itemInfo?.accessLevel);
  });
  return entityIdsWithAccess;
}
